import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Timer, AlertTriangle } from 'lucide-react';

import { FocusLossModal } from '@/components/competition/FocusLossModal';
import { ProgressBar } from '@/components/competition/ProgressBar';
import { QuestionCard } from '@/components/competition/QuestionCard';
import { PublicPageShell } from '@/components/layout/PageShell';
import { useAntiCheat } from '@/hooks/useAntiCheat';
import { useFetchWithRetry } from '@/hooks/useFetchWithRetry';
import { useCompetitionStore, useQuizAnswers, useQuizQuestions } from '@/store/competitionStore';
import {
  competitionFetch,
  persistProgress,
  TestCompletedElsewhereError,
  type SessionErrorCode,
} from '@/api/competitionApi';
import { useCompetitionCompletedGuard } from '@/hooks/useCompetitionCompletedGuard';
import { exitCompetitionToHome } from '@/hooks/useCompetitionSession';
import {
  broadcastProgress,
  subscribeProgress,
} from '@/lib/competitionProgressSync';
import type { QuestionsFetchResult, VerificationResponse } from '@/types';
import toast from 'react-hot-toast';

async function fetchQuestions(): Promise<QuestionsFetchResult> {
  const res = await competitionFetch('/api/v1/questions');
  if (!res.ok) {
    if (res.status === 403) {
      const body = await res.json().catch(() => ({})) as { code?: SessionErrorCode };
      if (body.code === 'ALREADY_COMPLETED') {
        throw new TestCompletedElsewhereError();
      }
    }
    throw new Error(`HTTP ${res.status}`);
  }
  const body = await res.json() as { success: boolean; data: QuestionsFetchResult['questions']; currentIndex?: number };
  return { questions: body.data, currentIndex: body.currentIndex };
}

function resolveResumeIndex(
  questionCount: number,
  apiIndex: number | undefined,
  storedIndex: number,
): number {
  if (typeof apiIndex === 'number' && apiIndex >= 0 && apiIndex < questionCount) {
    return apiIndex;
  }
  if (storedIndex >= 0 && storedIndex < questionCount) {
    return storedIndex;
  }
  return 0;
}

// ─── Deadline timer display ───────────────────────────────────────────────────

function calcTimeLeft(deadlineMs: number, offset: number): number {
  return Math.max(0, deadlineMs - (Date.now() + offset));
}

function formatMs(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

function TurniejLoadingCard({ message }: { message: string }) {
  return (
    <PublicPageShell mainClassName="items-center justify-center px-4 py-8">
      <div className="w-full max-w-lg rounded-3xl bg-white/90 px-8 py-10 text-center">
        <svg
          className="mx-auto h-14 w-14 animate-spin text-competition-accent"
          fill="none"
          viewBox="0 0 24 24"
          aria-label="Ładowanie..."
          role="status"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="3"
          />
          <path
            className="opacity-80"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        <p className="mt-5 text-base font-medium text-gray-500">{message}</p>
      </div>
    </PublicPageShell>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export const TurniejPage = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const testStarted = useCompetitionStore((s) => s.testStarted);
  const sessionReady = useCompetitionStore((s) => s.sessionReady);
  const deadlineMs = useCompetitionStore((s) => s.deadlineMs);
  const serverTimeOffset = useCompetitionStore((s) => s.serverTimeOffset);
  const setQuestions = useCompetitionStore((s) => s.setQuestions);
  const setAnswer = useCompetitionStore((s) => s.setAnswer);
  const setCurrentQuestionIndex = useCompetitionStore((s) => s.setCurrentQuestionIndex);
  const questions = useQuizQuestions();
  const answers = useQuizAnswers();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNoAnswerWarning, setShowNoAnswerWarning] = useState(false);
  const [timeLeftMs, setTimeLeftMs] = useState(() => calcTimeLeft(deadlineMs, serverTimeOffset));

  const timeoutFiredRef = useRef(false);
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;

  const isDevPreview = import.meta.env.DEV && new URLSearchParams(search).has('preview');
  const syncToastShownRef = useRef(false);

  const applyServerSync = useCallback(
    (data: VerificationResponse) => {
      if (!data.inProgress || questions.length === 0) return;

      if (data.savedAnswers) {
        for (const [qId, oId] of Object.entries(data.savedAnswers)) {
          setAnswer(qId, oId);
        }
      }

      if (typeof data.currentIndex === 'number') {
        const idx = resolveResumeIndex(
          questions.length,
          data.currentIndex,
          currentIndexRef.current,
        );
        if (idx !== currentIndexRef.current) {
          setCurrentIndex(idx);
          if (!syncToastShownRef.current) {
            syncToastShownRef.current = true;
            toast('Zsynchronizowano postęp z innej karty.', { duration: 3500 });
          }
        }
      }
    },
    [questions.length, setAnswer],
  );

  useCompetitionCompletedGuard({
    enabled: !isDevPreview && sessionReady,
    onActiveSync: applyServerSync,
  });

  useEffect(() => {
    if (isDevPreview) return undefined;
    return subscribeProgress((payload) => {
      const count = questions.length;
      if (count === 0) return;
      for (const [qId, oId] of Object.entries(payload.answers)) {
        setAnswer(qId, oId);
      }
      const idx = resolveResumeIndex(count, payload.currentIndex, currentIndexRef.current);
      if (idx !== currentIndexRef.current) {
        setCurrentIndex(idx);
      }
    });
  }, [isDevPreview, questions.length, setAnswer]);

  const flushPersist = useCallback(async (index: number) => {
    const snapshot = useCompetitionStore.getState().answers;
    await persistProgress(snapshot, index);
    broadcastProgress({ currentIndex: index, answers: snapshot });
  }, []);

  useEffect(() => {
    if (!testStarted && !isDevPreview) {
      navigate('/poczekalnia', { replace: true });
    }
  }, [testStarted, isDevPreview, navigate]);

  // Always-fresh ref to answers — prevents stale-closure bugs in effects
  // that run after async operations (question fetch, navigation).
  const answersRef = useRef(answers);
  answersRef.current = answers;

  useEffect(() => {
    setCurrentQuestionIndex(currentIndex);
  }, [currentIndex, setCurrentQuestionIndex]);

  const { showAlert, dismissAlert } = useAntiCheat({ enabled: sessionReady });

  // ─── Save progress on tab close / page reload ────────────────────────────
  // Calls /save-progress (NOT /submit-timeout) so the session is preserved
  // as IN_PROGRESS. The student can reopen the magic link and resume where
  // they left off — answers are restored from the database.
  //
  // sendBeacon is the only API that fires reliably during unload.
  // Custom headers are not supported so the token goes in the body.
  // Re-registers on every answers change so the closure is fresh.
  useEffect(() => {
    const handleUnload = () => {
      if (!sessionReady || Object.keys(answers).length === 0) return;
      navigator.sendBeacon(
        '/api/v1/save-progress',
        new Blob(
          [JSON.stringify({
            answers,
            currentIndex: currentIndexRef.current,
          })],
          { type: 'application/json' },
        ),
      );
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [answers, sessionReady]);

  const loadQuestions = useCallback(async () => {
    try {
      return await fetchQuestions();
    } catch (err) {
      if (err instanceof TestCompletedElsewhereError) {
        await exitCompetitionToHome('completed');
        navigate('/', { replace: true });
      }
      throw err;
    }
  }, [navigate]);

  const { data, fetchState, attempt, execute } = useFetchWithRetry<QuestionsFetchResult>(
    loadQuestions,
    {
      maxRetries: 3,
      retryDelayMs: 3000,
      shouldRetry: (err) => !(err instanceof TestCompletedElsewhereError),
    },
  );

  useEffect(() => {
    execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!data) return;

    // Server always returns this student's fixed question order
    // (persisted in TestSession.questionOrder on first fetch).
    setQuestions(data.questions);

    const storedIndex = useCompetitionStore.getState().currentQuestionIndex;
    const resumeIndex = resolveResumeIndex(
      data.questions.length,
      data.currentIndex,
      storedIndex,
    );
    setCurrentIndex(resumeIndex);
    setCurrentQuestionIndex(resumeIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, setQuestions, setCurrentQuestionIndex]);

  // ─── Server-authoritative deadline timer ──────────────────────────────────
  // Uses the same serverTimeOffset as the poczekalnia countdown so the client
  // clock cannot be manipulated to gain extra time.
  const handleTimeout = useCallback(async () => {
    if (timeoutFiredRef.current) return;
    timeoutFiredRef.current = true;
    setIsSubmitting(true);

    try {
      // Use the dedicated timeout endpoint — bypasses the deadline guard on
      // the server so partial answers are always saved.
      await competitionFetch('/api/v1/submit-timeout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      });
    } catch {
      // Best-effort — navigate regardless so the student can't stay on the form
    }

    navigate('/podziekowanie', { replace: true });
  }, [answers, navigate, isSubmitting]);

  useEffect(() => {
    if (deadlineMs === 0) return;

    const tick = setInterval(() => {
      const remaining = calcTimeLeft(deadlineMs, serverTimeOffset);
      setTimeLeftMs(remaining);

      if (remaining === 0 && !timeoutFiredRef.current) {
        clearInterval(tick);
        void handleTimeout();
      }
    }, 1000);

    return () => clearInterval(tick);
    // handleTimeout is stable (useCallback with stable deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadlineMs, serverTimeOffset]);

  // ─── Manual submit ────────────────────────────────────────────────────────

  async function handleSubmit() {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const res = await competitionFetch('/api/v1/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      });

      if (res.status === 409) {
        await exitCompetitionToHome('completed');
        navigate('/', { replace: true });
        return;
      }

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      navigate('/podziekowanie', { replace: true });
    } catch {
      setIsSubmitting(false);
      toast.error('Brak połączenia z Internetem. Sprawdź połączenie i spróbuj ponownie.');
    }
  }

  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const isUrgent = timeLeftMs < 5 * 60 * 1000; // last 5 minutes

  // ─── Loading / retry / error states ──────────────────────────────────────

  if (fetchState === 'loading') {
    return <TurniejLoadingCard message="Pobieranie pytań..." />;
  }

  if (fetchState === 'retrying') {
    return (
      <TurniejLoadingCard
        message={`Brak połączenia z serwerem. Próbuję pobrać pytania ponownie... (próba ${attempt}/3)`}
      />
    );
  }

  if (fetchState === 'error') {
    return (
      <PublicPageShell mainClassName="items-center justify-center px-4 py-8">
        <div className="w-full max-w-lg rounded-3xl bg-white/90 px-8 py-8 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="mt-4 text-lg font-bold text-gray-800">Nie udało się pobrać pytań</h2>
          <p className="mt-1 text-sm text-gray-500">
            Sprawdź połączenie z Internetem i spróbuj ponownie.
          </p>
          <button
            type="button"
            onClick={execute}
            className="mt-6 rounded-xl bg-competition-accent px-6 py-3 text-sm font-semibold text-white transition hover:bg-competition-accent-dark focus:outline-none focus:ring-2 focus:ring-competition-accent focus:ring-offset-2"
          >
            Spróbuj ponownie
          </button>
        </div>
      </PublicPageShell>
    );
  }

  if (!currentQuestion) {
    return <TurniejLoadingCard message="Przygotowuję pytania..." />;
  }

  // ─── Competition UI ───────────────────────────────────────────────────────

  return (
    <PublicPageShell mainClassName="items-center justify-center px-4 py-8">
      {showAlert && (
        <FocusLossModal onDismiss={dismissAlert} />
      )}
      <div className="w-full max-w-xl rounded-3xl bg-white/90 px-6 py-6 sm:px-8 sm:py-8">

        {/* Header + deadline timer */}
        <div className="mb-5 flex items-center justify-between gap-3">
          <h1 className="text-lg font-bold text-gray-800 sm:text-xl">
            Turniej Przetwórstwa Mleczarskiego
          </h1>
          {deadlineMs > 0 && (
            <div
              className={`shrink-0 flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-mono text-sm font-semibold tabular-nums ${
                isUrgent
                  ? 'bg-red-100 text-red-600 animate-pulse'
                  : 'bg-gray-100 text-gray-700'
              }`}
              aria-live="polite"
              title="Pozostały czas"
            >
              <Timer className="h-4 w-4" /> {formatMs(timeLeftMs)}
            </div>
          )}
        </div>

        {/* Progress */}
        <div className="mb-5">
          <ProgressBar
            current={currentIndex + 1}
            completed={(isSubmitting && isLastQuestion) ? questions.length : currentIndex}
            total={questions.length}
          />
        </div>

        {/* Question — niebieskawy panel */}
        <div
          className="rounded-2xl p-4 sm:p-5"
          style={{ backgroundColor: 'rgba(76, 197, 240, 0.18)' }}
        >
          <QuestionCard
            question={currentQuestion}
            selectedOptionId={answers[currentQuestion.id]}
            onSelect={(optionId) => {
              setAnswer(currentQuestion.id, optionId);
              setShowNoAnswerWarning(false);
            }}
          />
        </div>

        {/* Navigation — forward only, no going back */}
        <div className="mt-5 space-y-3">
          {showNoAnswerWarning && (
            <div
              role="alert"
              className="flex items-center gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5"
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100">
                <AlertTriangle className="h-3 w-3 text-amber-600" aria-hidden />
              </span>
              <p className="text-sm font-medium text-amber-800">
                Zaznacz odpowiedź przed przejściem dalej.
              </p>
            </div>
          )}
          <div className="flex justify-end">
            {isLastQuestion ? (
              <button
                onClick={() => {
                  if (!answers[currentQuestion.id]) {
                    setShowNoAnswerWarning(true);
                    return;
                  }
                  void handleSubmit();
                }}
                disabled={isSubmitting}
                className="rounded-xl bg-competition-accent px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-competition-accent-dark disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-competition-accent focus:ring-offset-2"
              >
                {isSubmitting ? 'Wysyłanie...' : 'Zakończ test'}
              </button>
            ) : (
              <button
                onClick={async () => {
                  if (isSubmitting) return;
                  if (!answers[currentQuestion.id]) {
                    setShowNoAnswerWarning(true);
                    return;
                  }
                  setShowNoAnswerWarning(false);
                  setIsSubmitting(true);
                  try {
                    const nextIndex = currentIndex + 1;
                    await flushPersist(nextIndex);
                    setCurrentIndex(nextIndex);
                  } catch (err) {
                    toast.error('Brak połączenia z Internetem. Sprawdź połączenie i spróbuj ponownie.');
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                disabled={isSubmitting}
                className="rounded-xl bg-competition-accent px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-competition-accent-dark disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-competition-accent focus:ring-offset-2"
              >
                {isSubmitting ? 'Zapisywanie...' : 'Następne →'}
              </button>
            )}
          </div>
        </div>

      </div>
    </PublicPageShell>
  );
};
